/**
 * compositor.js — places the matted subject onto the chosen background.
 *
 * The matte frame is an RGBA canvas with RGB = straight (non-premultiplied)
 * foreground and A = alpha matte. Drawing it source-over onto a background is
 * exactly the straight-alpha composite  out = fgr·α + bg·(1−α)  — RVM already
 * decontaminates fgr, so edges stay clean with no dark fringe.
 *
 * Background modes: transparent · color · gradient · image · blur.
 *   • transparent → the matte frame is returned unchanged (its alpha becomes
 *     the exported video's alpha channel).
 *   • blur → blurs the ORIGINAL source frame behind a sharp subject, so the
 *     pipeline passes sourceCanvas for this mode.
 *
 * Public API — window.VbrCompositor:
 *   composite(matteCanvas, bg, sourceCanvas?) → canvas
 *     bg: { mode, color, grad:{type,angle,c1,c2}, image, blurRadius }
 */
(function (root) {
    'use strict';

    function makeCanvas(w, h) {
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        return c;
    }

    function paintGradient(ctx, w, h, g) {
        var grad;
        if (g.type === 'radial') {
            grad = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) / 2);
        } else {
            var a = (g.angle || 135) * Math.PI / 180;
            var cx = w / 2, cy = h / 2, len = Math.max(w, h) / 2;
            grad = ctx.createLinearGradient(
                cx - Math.cos(a) * len, cy - Math.sin(a) * len,
                cx + Math.cos(a) * len, cy + Math.sin(a) * len);
        }
        grad.addColorStop(0, g.c1 || '#ff6b6b');
        grad.addColorStop(1, g.c2 || '#4ecdc4');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
    }

    function paintImageCover(ctx, w, h, img) {
        var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
        if (!iw || !ih) return;
        var ir = iw / ih, dr = w / h, sw, sh, sx, sy;
        if (ir > dr) { sh = ih; sw = ih * dr; sx = (iw - sw) / 2; sy = 0; }
        else         { sw = iw; sh = iw / dr; sx = 0; sy = (ih - sh) / 2; }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
    }

    function composite(matteCanvas, bg, sourceCanvas) {
        if (!bg || bg.mode === 'transparent') return matteCanvas;
        var w = matteCanvas.width, h = matteCanvas.height;
        var c = makeCanvas(w, h);
        var ctx = c.getContext('2d');

        if (bg.mode === 'color') {
            ctx.fillStyle = bg.color || '#00b140';
            ctx.fillRect(0, 0, w, h);
        } else if (bg.mode === 'gradient') {
            paintGradient(ctx, w, h, bg.grad || {});
        } else if (bg.mode === 'image' && bg.image) {
            paintImageCover(ctx, w, h, bg.image);
        } else if (bg.mode === 'blur' && sourceCanvas) {
            var pad = Math.ceil((bg.blurRadius || 10) * 2);
            ctx.save();
            ctx.filter = 'blur(' + (bg.blurRadius || 10) + 'px)';
            ctx.drawImage(sourceCanvas, -pad, -pad, w + pad * 2, h + pad * 2);
            ctx.restore();
        } else {
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, w, h);
        }

        // Subject on top — source-over with straight alpha = correct composite.
        ctx.drawImage(matteCanvas, 0, 0);
        return c;
    }

    root.VbrCompositor = { composite: composite };
})(typeof window !== 'undefined' ? window : globalThis);
